import { ReactNode, useEffect, useState } from 'react';
import { DataFilterNumberRangeValue } from '@asset-metrix/portal-gateway-client-js';
import { NumberRange } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { NumberRangeFilterData } from '../../store/states/filterTypes';

export type RangeNumberPickerFilterProps = {
  allowDecimal?: boolean;
  clearable?: boolean;
  entityId: string;
  factor?: number;
  filterData?: NumberRangeFilterData;
  filterKey: string;
  isFetching: boolean;
  label?: ReactNode | ReactNode[];
  suffix?: string;
};

function selectionToNumberRange(
  selection?: DataFilterNumberRangeValue
): NumberRange {
  return {
    from: selection?.start,
    to: selection?.end,
  };
}

function numberRangeEntryToNumberOrUndefined(
  input: string | number | undefined
): number | undefined {
  if (input == null) {
    return input;
  }
  if (typeof input === 'string') {
    if (input === '') {
      return undefined;
    }

    return Number.parseFloat(input);
  }

  return input;
}

function numberRangeToSelection(
  numberRange: NumberRange
): DataFilterNumberRangeValue {
  return {
    start: numberRangeEntryToNumberOrUndefined(numberRange.from),
    end: numberRangeEntryToNumberOrUndefined(numberRange.to),
  };
}

export function RangeNumberPickerFilter({
  allowDecimal,
  clearable,
  entityId,
  factor,
  filterData,
  filterKey,
  isFetching,
  label,
  suffix,
}: RangeNumberPickerFilterProps) {
  const [value, setValue] = useState(
    selectionToNumberRange(filterData?.selection)
  );
  useEffect(() => {
    setValue(selectionToNumberRange(filterData?.selection));
  }, [filterData?.selection, setValue]);

  const [setFilterSelection, resetFilterSelection] = useStore(
    useShallow((state) => [
      state.setFilterSelection,
      state.resetFilterSelection,
    ])
  );

  return (
    <NumberRange
      allowDecimal={allowDecimal}
      clearable={clearable}
      factor={factor}
      label={label}
      loading={isFetching}
      onChange={setValue}
      onCommit={(range) => {
        const selection = numberRangeToSelection(range);
        if (selection.start == null && selection.end == null) {
          return resetFilterSelection(entityId, filterKey);
        }
        setFilterSelection(
          entityId,
          filterKey,
          'number',
          'range-number-picker',
          selection
        );
      }}
      suffix={suffix}
      value={value}
    />
  );
}
